import { useDisclosure } from '@mantine/hooks';
import DeviceQrPairingModal, {
  DeviceQrPairingModalProps,
} from 'components/modals/device-qr-pairing/DeviceQrPairingModal';
import { FEATURE_TOGGLE_DEVICE_QR_CODES } from 'env';
import { createContext, useContext, useMemo, useState } from 'react';

type OpenParams = DeviceQrPairingModalProps extends infer Props
  ? Props extends DeviceQrPairingModalProps
    ? Omit<Props, 'opened' | 'onClose'>
    : never
  : never;

interface DeviceQrPairingModalContextValue {
  openDeviceQrPairingModal: (params: OpenParams) => void;
}

const DeviceQrPairingModalContext = createContext<DeviceQrPairingModalContextValue>(undefined!);

/** Provides the global device QR pairing workflow. */
export function DeviceQrPairingModalProvider({ children }: { children: React.ReactNode }) {
  const [opened, { open, close }] = useDisclosure(false);
  const [params, setParams] = useState<OpenParams>();

  /** Opens pairing only while the rollout toggle is enabled. */
  const openDeviceQrPairingModal = (nextParams: OpenParams) => {
    if (!FEATURE_TOGGLE_DEVICE_QR_CODES) {
      return;
    }

    setParams(nextParams);
    open();
  };

  const value = useMemo(() => ({ openDeviceQrPairingModal }), [openDeviceQrPairingModal]);

  return (
    <DeviceQrPairingModalContext.Provider value={value}>
      {children}
      {FEATURE_TOGGLE_DEVICE_QR_CODES && opened && params && (
        <DeviceQrPairingModal {...params} opened onClose={close} />
      )}
    </DeviceQrPairingModalContext.Provider>
  );
}

/** Returns the device QR pairing modal controller. */
export function useDeviceQrPairingModal() {
  return useContext(DeviceQrPairingModalContext);
}
